import React, {createContext, useContext, useImperativeHandle} from 'react';

import {useAttributeSets, type UseAttributeSetsParams} from '../hooks';

export type AttributeSetsContextType = ReturnType<typeof useAttributeSets>;

const AttributeSetsContext = createContext<
	AttributeSetsContextType | undefined
>(undefined);

export const useAttributeSetsProvider = () => {
	const context = useContext(AttributeSetsContext);
	if (context === undefined) {
		throw new Error(
			'`useAttributeSetsProvider` must be used within a `AttributeSetsProvider`',
		);
	}
	return context;
};

export type AttributeSetsProviderRef =
	| React.RefObject<AttributeSetsContextType>
	| ((node: AttributeSetsContextType) => void)
	| null;

export type AttributeSetsProviderChildrenProps = AttributeSetsContextType;

export type AttributeSetsProviderProps = UseAttributeSetsParams & {
	ref?: AttributeSetsProviderRef;
	children:
		| React.ReactNode
		| ((props: AttributeSetsProviderChildrenProps) => React.ReactNode);
};

const AttributeSetsProviderInner = (
	{children, ...props}: AttributeSetsProviderProps,
	ref: AttributeSetsProviderRef,
) => {
	const attributeSets = useAttributeSets(props);

	useImperativeHandle(ref, () => attributeSets, [attributeSets]);

	return (
		<AttributeSetsContext.Provider value={attributeSets}>
			{typeof children === 'function' ? children(attributeSets) : children}
		</AttributeSetsContext.Provider>
	);
};

export const AttributeSetsProviderPrimitive = React.forwardRef<
	AttributeSetsContextType,
	AttributeSetsProviderProps
>(AttributeSetsProviderInner);

/**
 * Attribute sets context provider
 * @example
 * <AttributeSetsProvider ref={attributeSetsRef}>
 * 	...
 * </AttributeSetsProvider>
 * @example
 * <AttributeSetsProvider ref={attributeSetsRef}>
 * 	{(attributeSets) => (
 * 		...
 * 	)}
 * </AttributeSetsProvider>
 */
export const AttributeSetsProvider = React.memo(AttributeSetsProviderPrimitive);

export default AttributeSetsProvider;
